import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from '@reduxjs/toolkit';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { concat, get, uniqBy } from 'lodash';

import { isVipFacility } from 'utils/facility';
import { isFoundationMedium } from 'utils/breakpoints';
import { useViewportWidth } from 'decorators/withViewportWidth';

import { H2 } from 'components/core/Headline';

import { fetchSearchResults } from 'store/search/slice';
import { createBooking, createLead } from 'store/bookings/slice';
import { facilityPageSelector } from 'store/selectors/facilities';
import { getSearchFacilities } from 'store/selectors/search';
import { getFacilityHoursAndDirection } from 'store/facilities/slice';
import {
	getLocalPhoneNumberByZipCode,
	getTollfreePhoneNumber,
	getLocalPhoneNumberByCityState,
} from 'store/pageMeta/slice';
import { OONFacilityPage } from 'pages/OONFacilityPage';
import { withPageViewTracking } from 'components/core/library/tracking-utils';
import { withHoursAndDirectionsHandlers } from 'decorators/facility';
import {
	useWebPageSchema,
	withSearchHandler,
	withSelfStorage,
} from 'decorators';
import { Page } from 'components/core/Page';
import { FacilityDetails } from 'components/facility/FacilityDetailsV2';
import { FacilityBreadcrumbs } from 'components/facility/FacilityBreadcrumbsV2';
import { FilterableUnitList } from 'components/facility/FilterableUnitListV2';
import { FacilityReviewsV2 } from 'components/facility/FacilityReviewsV2';
import { BackToTop } from 'components/core/BackToTop';
import { scrollToElement, setLocation } from 'lib/helpers';
import { GiftCardBanner } from 'components/Banners/GiftCardBanner';
import GiftCardModalLink from 'components/giftcard/GiftCardModalLink/GiftCardModalLink';
import { FacilityUnitMicroData } from 'components/facility/FacilityUnitMicroDataV2';

import { NewFourOhFourPage } from 'pages/NewFourOhFourPage';
import InactiveFacilityPage from 'pages/FacilityPage/InactiveFacilityPage';
import './FacilityPageV2.scss';
import 'components/psp/PSPLayout/PSPLayout.scss';
import { TrackingClient } from '@sparefoot/tracking-client';
import { PAGE_TYPES } from '@sparefoot/tracking-events';
import { trackCTAClick } from '../../components/core/library/tracking-utils/tracking/clickTrackingUtils';

import { encode } from 'querystring';
import useExperiment from 'hooks/useExperiment';
import StorageCalculatorEmbed from 'components/calculator/StorageCalculatorEmbed';
import TopBanner from '../../components/Banners/TopBanner/TopBanner';
import FacilityGallery from '../../components/facility/FacilityGallery/FacilityGallery';
import StorageNearYou from '../../components/organisms/StorageNearYou/StorageNearYou';
import HowItWorksV2 from '../../components/facility/HowItWorksV2/HowItWorksV2';
import omiIcon from 'assets/images/icons/OMI.svg';
import discountIcon from 'assets/images/icons/discount.svg';
import questionMarkIcon from 'assets/images/icons/question-mark.svg';
import FacilityAmenitiesRanked from '../../components/facility/FacilityAmenitiesRanked/FacilityAmenitiesRanked';
import { FeaturesAmenitiesAccordion } from 'components/facility/FeaturesAmenitiesAccordion/FeaturesAmenitiesAccordion';
import chevronLeftIcon from 'assets/images/icons/chevron-left.svg';

const hash = process.env.REACTFOOT_VERSION; // Extract the hash from the env variable
const cssUrl = `https://philes.sparefoot.com/assets/${hash}/Map.css`;

const loadMapCss = (url) => {
	if (typeof window !== 'undefined' && typeof document !== 'undefined') {
		const link = document.createElement('link');
		link.rel = 'stylesheet';
		link.href = url;
		link.onload = () => console.log(`Loaded map css on page load`);
		link.onerror = (err) => console.error(`Failed to load map css`, err);
		document.head.appendChild(link);
	}
};

const withProps = (propsOrFn) => (Component) => {
	const WithProps = (props) => {
		const extra =
			typeof propsOrFn === 'function' ? propsOrFn(props) : propsOrFn;
		return (
			<Component
				{...props}
				{...extra}
			/>
		);
	};
	return WithProps;
};

const branch = (test, left, right) => (Component) => (props) => {
	if (test(props)) {
		const Left = left(Component);
		return <Left {...props} />;
	}
	if (right) {
		const Right = right(Component);
		return <Right {...props} />;
	}
	return <Component {...props} />;
};

const renderComponent = (X) => () => X;

const renderNothing = () => () => null;

function withFacilityPageV2State(Component) {
	function FacilityPageV2WithState(ownProps) {
		const dispatch = useDispatch();
		const selectorResult = useSelector((state) => ({
			// pass component props into the selector so route params (like
			// match.params.facilityId) are available when computing the
			// facility page props
			...facilityPageSelector(state, ownProps),
			googleMapsKey: state.app.googleMapsKey,
			// Include search facilities for map updates (properly mapped with units)
			searchFacilities: getSearchFacilities(state),
		}));
		const actions = useMemo(
			() => bindActionCreators({
				fetchSearchResults,
				createBooking,
				createLead,
				getFacilityHoursAndDirection,
				getLocalPhoneNumberByZipCode,
				getLocalPhoneNumberByCityState,
				getTollfreePhoneNumber,
			}, dispatch),
			[dispatch],
		);
		return <Component {...ownProps} {...selectorResult} actions={actions} />;
	}
	FacilityPageV2WithState.displayName = 'FacilityPageV2WithState';
	return FacilityPageV2WithState;
}

export const enhance = (Component) =>
	withFacilityPageV2State(
		branch(
			(props) => !props.facility,
			renderNothing,
		)(
			branch(
				(props) => props.facility.isOutOfNetwork,
				renderComponent(OONFacilityPage),
			)(
				branch(
					(props) => props.facility.isInactive,
					(C) =>
						withProps({ statusCode: 410 })(
							renderComponent(NewFourOhFourPage)(C),
						),
				)(
					withPageViewTracking(PAGE_TYPES.FACILITY)(
						withHoursAndDirectionsHandlers(
							withProps(
								({
									queryParams,
									facility,
									storageUnits,
									vehicleUnits,
									searchFacilities,
									nearbyFacilities,
								}) => ({
									searchType: get(
										queryParams,
										'searchType',
										'storage',
									),
									unitSizeFilters: concat(
										[],
										get(queryParams, 'unitSizeFilters', []),
									),
									scrollToReviews: () =>
										scrollToElement('.facility-reviews'),
									scrollToUnits: () =>
										scrollToElement('#facility-units'),
									scrollToStorageCalculator: () =>
										scrollToElement(
											'.calculator-content-container',
										),
									storageUnits: concat(
										storageUnits || [],
										vehicleUnits || [],
									),
									facilityBadges: facility.badges,
									facilitiy: facility,
									searchFacilities: uniqBy(
										concat(
											searchFacilities || [],
											nearbyFacilities || [],
											{
												...facility,
												units: concat(
													storageUnits || [],
													vehicleUnits || [],
												),
											},
										),
										'id',
									).map((f) => ({
										...f,
										units: (f.units || []).slice(0, 3),
									})),
									facilitySearchLocation: {
										lat: parseFloat(
											facility.location.latitude,
										),
										lng: parseFloat(
											facility.location.longitude,
										),
										latitude: facility.location.latitude,
										longitude: facility.location.longitude,
									},
								}),
							)(
								withSelfStorage(
									withSearchHandler(
										branch(
											(props) =>
												props.facilityIsInactive,
											renderComponent(
												InactiveFacilityPage,
											),
										)(Component),
									),
								),
							),
						),
					),
				),
			),
		),
	);

export const FacilityPageV2 = ({ images = [], actions = {}, ...restProps }) => {
	const { viewportWidth } = useViewportWidth();
	const isMobile = viewportWidth !== 0 && !isFoundationMedium(viewportWidth);
	const baseProps = { images, actions, ...restProps };
	const webPageSchemaProps = useWebPageSchema(baseProps);
	const props = { ...baseProps, ...webPageSchemaProps };
	useExperiment('sf_15309_giftcard_incentives');
	const [selectedUnit, setSelectedUnit] = useState(null);
	const [selectedStorageType, setSelectedStorageType] = useState(null);
	const reservationFormRef = React.useRef(null);

	const handleToggleUnitReservationDrawer = (unit, storageType) => {
		// There is no selected unit when the user opens the drawer; only closing, so this only sends when
		// a user clicks the CTA to book a unit, not closing the drawer
		if (!selectedUnit) {
			let clickUrl = `${props?.match?.url}?${encode(props?.queryParams)}`;

			// Only fire CTA click when drawer is opened
			trackCTAClick(
				'render-reservation-form-button',
				'Continue Button',
				'Continue',
				clickUrl,
			);

			let { facility } = props;

			TrackingClient.trackFacilityUnitSelected({
				facility: {
					id: unit?.facilityId,
					name: facility?.name,
					location: facility?.location?.text,
				},
				last_unit_added_date: new Date(),
				unit: {
					currency: 'usd',
					id: unit?.id,
					variant_size: unit?.sizeText,
					price: unit?.price?.text,
					quantity: 1,
				},
				unit_id: unit.id,
				unit_promo: unit.promotion,
			});

			// Navigate to checkout page instead of showing form
			const facilityUrl = facility.url?.facility || '';
			const urlMatch = facilityUrl.match(
				/\/([^/]+)-([A-Z]{2})-self-storage\/([^/]+)-(\d+)/,
			);

			if (urlMatch) {
				const [, city, state, facilitySlug] = urlMatch;
				const reservationUrl = `/${city}-${state}-self-storage/${facilitySlug}-${facility.id}/reserve/${unit.id}`;
				setLocation(reservationUrl);
				return; // Don't update state, we're navigating away
			}
		}

		// reset the movein date whenever someone opens a new reservation form
		if (reservationFormRef.current) {
			reservationFormRef.current.setValue('moveInDate', '');
		}

		const newSelectedUnit =
			selectedUnit === unit.id && selectedStorageType === storageType
				? null
				: unit.id;
		const newSelectedStorageType =
			selectedUnit === unit.id && selectedStorageType === storageType
				? null
				: storageType;
		setSelectedUnit(newSelectedUnit);
		setSelectedStorageType(newSelectedStorageType);
	};

	const handleSearchParamChange = (newParams) => {
		// Fetch facilities for the new map area but stay on facility page
		if (newParams.latitude && newParams.longitude && newParams.boundary) {
			// Dispatch the search action to fetch facilities for the new bounds
			props.actions.fetchSearchResults({
				latitude: newParams.latitude,
				longitude: newParams.longitude,
				boundary: newParams.boundary,
				page: 1,
			});
		}
	};

	useEffect(() => {
		if (props.facility) {
			const { facility } = props;
			loadMapCss(cssUrl);

			TrackingClient.trackFacilityViewed({
				facility_badge: `${facility.badges}`,
				facility_id: facility.id,
				facility_name: facility.name,
				facility_promo: facility.promotion,
				facility_rank: '', // TODO: Potentially use queryParam to get rank from search page?
			});
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	if (!props.facility) {
		return (
			<Page
				className="facility-page-v2"
				pageType={props.pageType}
			/>
		);
	}

	const { facilityBadges: rawBadges = [], geolocation = {}, nearbyCities = [] } = props;
	const showOmiBadge = rawBadges.includes('omi');
	const facilityBadges = showOmiBadge
		? rawBadges.map((b) => (b === 'omi' ? 'omiBadge' : b))
		: rawBadges;

	const discounts = props?.facility?.groupedAmenities?.discounts?.items || {};

	// Filter only discounts where hasAttribute is strictly true
	const availableDiscounts = Object.entries(discounts).filter(
		([, value]) => value?.hasAttribute === true,
	);

	const googleReviews = props.facility.googleReviews
		? props.facility.googleReviews
		: {};
	const showGoogleReviews = !!(
		googleReviews?.reviews && googleReviews?.reviews.length > 0
	);

	const qualityAvg = [
		{
			name: 'Price',
			value:
				props.facility.pros && props.facility.pros['Good Value Agg']
					? props.facility.pros['Good Value Agg']
					: 0,
		},
		{
			name: 'Service',
			value:
				props.facility.pros && props.facility.pros['Good Service Agg']
					? props.facility.pros['Good Service Agg']
					: 0,
		},
		{
			name: 'Cleanliness',
			value:
				props.facility.pros && props.facility.pros['CleanAgg']
					? props.facility.pros['CleanAgg']
					: 0,
		},
	];

	return (
		<Page
			className="facility-page-v2 in-network-facility-page"
			pageType={props.pageType}
		>
			<section>
				<div className="content-container breadcrumb-container">
					{isMobile ? (
						<button
							className="back-button"
							onClick={() => window.history.back()}
							aria-label="Go back"
						>
							<div className="arrow-icon">
								<img
									src={chevronLeftIcon}
									alt="Back Arrow"
								/>
							</div>
							<span>Back</span>
						</button>
					) : (
						<FacilityBreadcrumbs
							facility={props.facility}
							canonicalUrl={props.canonicalUrl}
						/>
					)}
				</div>
				<section className="main-content">
					{props.scarcityShow && (
						<section className="content-container">
							<TopBanner
								title="Reserve Now!"
								content="Pricing and availability may change."
							/>
						</section>
					)}
					<section className="row content-container">
						<FacilityDetails
							facilityName={props.facility.name}
							facilityId={props.facility.id}
							facilityZipCode={props.facility.location.zip}
							locationText={props.facility.location.text}
							isVipFacility={isVipFacility(props.facility)}
							googleReviewsEnabled={
								props.facility.isGoogleReviewEnabled
							}
							googleReviewsCount={get(
								props.facility,
								'googleReviews.userRatingsTotal',
							)}
							googleReviewsAverage={get(
								props.facility,
								'googleReviews.rating',
							)}
							reviewAverage={get(
								props.facility,
								'reviewMetadata.average',
							)}
							reviewCount={get(
								props.facility,
								'reviewMetadata.count',
							)}
							facilityPhoneNumber={
								props.phone.localSparephone ||
								props.phone.nationalSparephone
							}
							unformattedFacilityPhoneNumber={
								props.phone.localSparephoneUnformatted ||
								props.phone.nationalSparephoneUnformatted
							}
							onGetHoursAndDirections={
								props.actions.getFacilityHoursAndDirection
							}
							getLocalPhoneNumberByZipCode={
								props.actions.getLocalPhoneNumberByZipCode
							}
							getLocalPhoneNumberByCityState={
								props.actions.getLocalPhoneNumberByCityState
							}
							scrollToReviews={props.scrollToReviews}
						/>
					</section>
					<section className="facility-image-container">
						<FacilityGallery
							facility={props.facility}
							images={props.images}
							googleMapsKey={props.googleMapsKey}
							facilities={props.searchFacilities}
							searchLocation={props.facilitySearchLocation}
							phone={props.phone}
							preload={true}
							getLocalPhoneNumberByZipCode={
								props.actions.getLocalPhoneNumberByZipCode
							}
							onSearch={handleSearchParamChange}
							onReady={() => {}}
						/>
					</section>
					<FacilityAmenitiesRanked
						groupedAmenities={props.facility.groupedAmenities}
						storageUnits={props.storageUnits}
						vehicleUnits={props.vehicleUnits}
					/>
				</section>
			</section>

			<section className="highlighted-content facility-page-banners-cards-container">
				{(availableDiscounts?.length > 0 ||
					facilityBadges.includes('omiBadge')) && (
					<section className="discount-banners">
						<section className="row content-container">
							{facilityBadges.includes('omiBadge') && (
								<TopBanner
									className="top-banner_omi"
									icon={omiIcon}
									iconAlt="Online Move In Icon"
									title="Online Move-In Available"
									content="Rent online, skip the counter, and go straight to your space"
								/>
							)}
						</section>
						{availableDiscounts.length > 0 && (
							<section className="row content-container">
								{availableDiscounts.map(
									([key, value], index) => {
										const cleanLabel = value.label
											.split('(')[0]
											.trim();
										// Calculate responsive column class
										const columnClass =
											availableDiscounts.length === 1
												? 'column-full full_banner'
												: availableDiscounts.length ===
													  2
													? index === 0
														? 'column-left'
														: 'column-right'
													: [
															'column-left',
															'column-center',
															'column-right',
														][index] || '';

										return (
											<section
												className={`split-column ${columnClass}`}
												key={key}
											>
												<TopBanner
													className="top-banner_discount"
													icon={discountIcon}
													iconAlt="Discount Icon"
													title={cleanLabel}
													tooltipIcon={
														questionMarkIcon
													}
													tooltipMessage={value.label}
												/>
											</section>
										);
									},
								)}
							</section>
						)}
					</section>
				)}
				<section className="content-container reservation-block">
					<FacilityUnitMicroData
						storageUnits={props.storageUnits}
						vehicleUnits={props.vehicleUnits}
						facilityName={props.facility.name}
						facilityUrl={props.facility.url.facility}
						facilityImageUrl={props.facility.images?.[0]?.src || ''}
						facilityId={props.facility.id}
						canonicalUrl={props.canonicalUrl}
					/>
					<div
						className={classnames({
							'vehicle-first': props.searchType === 'vehicle',
						})}
					>
						<div key="storage-units-group">
							{props.storageUnits &&
								!!props.storageUnits.length &&
								props.facility.giftCardOffer?.giftCardValue && (
									<GiftCardBanner
										className="facility-gift-card-banner"
										content={<GiftCardModalLink />}
									/>
								)}
							<FilterableUnitList
								units={props.storageUnits}
								unitSizeFilters={props.unitSizeFilters}
								searchType={props.searchType}
								facility={props.facility}
								scrollToStorageCalculator={
									props.scrollToStorageCalculator
								}
								phone={props.phone}
								unitDetailsComponentProps={(unit) => ({
									handleToggleUnitReservationDrawer,
									onCreateBooking:
										props.actions.createBooking,
									storageType: 'selfStorage',
									reservationFormShowing:
										selectedUnit === unit.id &&
										selectedStorageType === 'selfStorage',
									facilityImages: props.images,
									reservationFormRef,
								})}
							/>
						</div>
					</div>
				</section>
			</section>
			<section className="content-container">
				<section className="features-faq">
					<H2 className="features-faq-header">Facility Details</H2>
					<FeaturesAmenitiesAccordion facility={props.facility} />
				</section>
			</section>
			<section className="calculator-content-container">
				<StorageCalculatorEmbed type="facility" />
			</section>

			<section
				className="reviews-content-wrapper"
				id="reviews"
			>
				<section className="content-container">
					<FacilityReviewsV2
						reviews={props.facility.reviews}
						reviewAverage={get(
							props.facility,
							'reviewMetadata.average',
							0,
						)}
						reviewCount={get(
							props.facility,
							'reviewMetadata.count',
							0,
						)}
						ratingCounts={get(
							props.facility,
							'reviewMetadata.ratingCounts',
						)}
						pros={qualityAvg}
						cons={get(props.facility, 'reviewMetadata.cons')}
						recommendedCount={get(
							props.facility,
							'reviewMetadata.recommendedCount',
						)}
						recommendedRate={get(
							props.facility,
							'reviewMetadata.recommendedRate',
						)}
						facilityId={props.facility.id}
						facilityUrl={props.facility.url.facility}
						fullFacilityUrl={props.fullFacilityUrl}
						currentReviewPage={props.reviewPage}
						images={get(props, 'images', []).map(
							(image) => image.src,
						)}
						facilityName={get(props, 'facility.name')}
						locationText={props.facility.location.text}
						googleReviews={googleReviews}
						showGoogleReviews={showGoogleReviews}
					/>
				</section>
			</section>
			<section className="how-it-works">
				<HowItWorksV2 />
			</section>
			<section className="content-container">
				<section className="facility-storage-near-you">
					<StorageNearYou
						geolocation={geolocation}
						nearbyCities={nearbyCities}
					/>
				</section>
			</section>
			<BackToTop />
			<script
				key="schema"
				type="application/ld+json"
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{ __html: props.pageSchemaV2 }}
			/>
			<script
				key="WebPage"
				type="application/ld+json"
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{ __html: props.webPageSchemaV2 }}
			/>
			<script
				key="SelfStorage"
				type="application/ld+json"
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{ __html: props.selfStorageSchema }}
			/>
		</Page>
	);
};

FacilityPageV2.propTypes = {
	actions: PropTypes.shape({
		createBooking: PropTypes.func,
		fetchSearchResults: PropTypes.func,
		getFacilityHoursAndDirection: PropTypes.func,
		getLocalPhoneNumberByZipCode: PropTypes.func.isRequired,
		getLocalPhoneNumberByCityState: PropTypes.func.isRequired,
	}),
	facility: PropTypes.object,
	images: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string.isRequired,
			alt: PropTypes.string,
		}),
	),
	match: PropTypes.shape({
		url: PropTypes.string,
	}),
	queryParams: PropTypes.object,
	canonicalUrl: PropTypes.string,
	scrollToReviews: PropTypes.func,
	scrollToStorageCalculator: PropTypes.func,
	searchFacilities: PropTypes.array,
	pageSchemaV2: PropTypes.string,
	webPageSchemaV2: PropTypes.string,
	handleToggleUnitReservationDrawer: PropTypes.func,
	phone: PropTypes.object,
	selectedUnit: PropTypes.number,
	selectedStorageType: PropTypes.string,
	storageUnits: PropTypes.arrayOf(PropTypes.object),
	searchType: PropTypes.string,
	unitSizeFilters: PropTypes.arrayOf(PropTypes.string),
	vehicleUnits: PropTypes.arrayOf(PropTypes.object),
	reviewPage: PropTypes.number,
	fullFacilityUrl: PropTypes.string,
	facilityIsInactive: PropTypes.bool,
	nearbyFacilities: PropTypes.arrayOf(PropTypes.object),
	nearbyCities: PropTypes.arrayOf(PropTypes.object),
	scarcityShow: PropTypes.bool,
	facilityBadges: PropTypes.arrayOf(PropTypes.string),
	geolocation: PropTypes.shape({
		city: PropTypes.string,
		region: PropTypes.string,
	}),
	pageSchema: PropTypes.string,
	offerCatalogSchema: PropTypes.string,
	mapSchema: PropTypes.string,
	selfStorageSchema: PropTypes.string,
	pageType: PropTypes.string,
	hasOnlineMoveIns: PropTypes.bool,
	sf11786Variation: PropTypes.string,
	googleMapsKey: PropTypes.string,
	facilitySearchLocation: PropTypes.object,
	preselectedFacilityId: PropTypes.number,
};

export default enhance(FacilityPageV2);
